import type { IconName } from "@/components/icons/Icon";
import { FeatureFlagLiteral } from "@/utils/hooks/use-flags-grpc";
import { Routes } from "@/utils/routes";

export interface TeamNavbarLink<IdType extends string> {
  href: () => string;
  text: string;
  /** Extra command-palette query terms for concepts this page owns but does not name. */
  searchTerms?: readonly string[];
  id: IdType;
  admin?: boolean;
  flag?: FeatureFlagLiteral;
  icon?: IconName;
}

export const shouldShowTeamNavbarLink = <IdType extends string>(
  link: TeamNavbarLink<IdType>,
  enabled: (flag: FeatureFlagLiteral) => boolean,
  canSeeAdminLinks: boolean,
) => {
  if (link.admin && !canSeeAdminLinks) return false;
  if (link.flag != null) {
    return enabled(link.flag) || canSeeAdminLinks;
  }
  return true;
};

export type TeamNavbarPageId =
  | "projects"
  | "cloud-accounts"
  | "cloud-overview"
  | "site-overview"
  | "migration-status"
  | "signup-codes"
  | "signup-links"
  | "license-keys"
  | "cloud-vpcs"
  | "cloud-clusters"
  | "cloud-container-registries"
  | "cloud-storage"
  | "recovery-clusters"
  | "audit-logs"
  | "settings"
  | "sso";

/**
 * Links that belong to the team navbar's "Admin" menu rather than its flat list. Two
 * surfaces read this: the team navbar groups them into the menu, and the project navbar
 * excludes them from its workspace dropdown so they appear in exactly one place.
 *
 * These deliberately remain ordinary entries in TEAM_NAVBAR_LINKS_V2 rather than moving to
 * a separate list. That list doubles as the registry of team destinations: TeamTopRailV2
 * resolves the current page's breadcrumb label out of it by id, and the command palette
 * searches it. Removing them would leave these pages with a breadcrumb reading only
 * "Global settings" and drop them from palette search, neither of which is intended.
 *
 * Only ids that are already `admin: true` belong here; useTeamNavbarLinks is what filters
 * the whole set down to Chalk employees.
 */
export const TEAM_NAVBAR_ADMIN_SECTION_ITEM_IDS: readonly TeamNavbarPageId[] = [
  "migration-status",
  "signup-codes",
  "signup-links",
  "license-keys",
];

export const TEAM_NAVBAR_LINKS: TeamNavbarLink<TeamNavbarPageId>[] = [
  {
    href: () => Routes.projects,
    text: "Projects",
    id: "projects",
    icon: "folder",
  },
  {
    href: () => Routes.global.cloudAccounts,
    text: "Cloud Accounts",
    id: "cloud-accounts",
    icon: "cloudServer",
  },
  {
    href: () => Routes.global.cloudOverview,
    text: "Cloud overview",
    id: "cloud-overview",
    icon: "boxes",
  },
  {
    href: () => Routes.global.cloudVpcs,
    text: "Cloud VPCs",
    id: "cloud-vpcs",
    icon: "stack",
  },
  {
    href: () => Routes.global.cloudClusters,
    text: "Cloud Clusters",
    id: "cloud-clusters",
    icon: "hexagon",
  },
  {
    href: () => Routes.global.cloudContainerRegistries,
    text: "Container registries",
    id: "cloud-container-registries",
    icon: "database",
    admin: true,
  },
  {
    href: () => Routes.global.cloudStorage,
    text: "Cloud storage",
    id: "cloud-storage",
    icon: "database",
    admin: true,
  },
  {
    href: () => Routes.global.licenseKeys,
    text: "License keys",
    id: "license-keys",
    icon: "key",
    admin: true,
  },
  {
    href: () => Routes.team.auditLogs,
    text: "Audit logs",
    id: "audit-logs",
    icon: "history",
  },
  {
    href: () => Routes.team.settings(),
    text: "Settings",
    id: "settings",
    icon: "settings",
  },
];

export const TEAM_NAVBAR_LINKS_V2: TeamNavbarLink<TeamNavbarPageId>[] = [
  {
    href: () => Routes.projects,
    text: "Projects",
    id: "projects",
    icon: "folder",
  },
  {
    href: () => Routes.global.cloudAccounts,
    text: "Accounts",
    id: "cloud-accounts",
    icon: "clouds",
  },
  {
    href: () => Routes.global.siteOverview,
    text: "Site overview",
    id: "site-overview",
    icon: "binoculars",
    admin: true,
  },
  {
    href: () => Routes.global.migrationStatusList,
    text: "Migrations",
    id: "migration-status",
    icon: "checkSquare",
    admin: true,
  },
  {
    href: () => Routes.global.signupCodes,
    text: "Signup codes",
    id: "signup-codes",
    icon: "key",
    admin: true,
  },
  {
    href: () => Routes.global.signupLinks,
    text: "Signup links",
    id: "signup-links",
    icon: "key",
    admin: true,
  },
  {
    href: () => Routes.global.licenseKeys,
    text: "License keys",
    id: "license-keys",
    icon: "key",
    admin: true,
  },
  {
    href: () => Routes.global.cloudOverview,
    text: "Cloud overview",
    id: "cloud-overview",
    icon: "clouds",
  },
  {
    href: () => Routes.global.cloudVpcs,
    text: "VPCs",
    id: "cloud-vpcs",
    icon: "stack",
  },
  {
    href: () => Routes.global.cloudClusters,
    text: "Clusters",
    searchTerms: ["host pools"],
    id: "cloud-clusters",
    icon: "servers",
  },
  {
    href: () => Routes.global.cloudContainerRegistries,
    text: "Registries",
    id: "cloud-container-registries",
    icon: "package",
  },
  {
    href: () => Routes.global.cloudStorage,
    text: "Storage",
    id: "cloud-storage",
    icon: "archive",
  },
  {
    href: () => Routes.global.recoveryClusters,
    text: "Recovery",
    id: "recovery-clusters",
    icon: "lifeBuoy",
    flag: "recovery_cluster_management_enabled",
  },
  {
    href: () => Routes.team.auditLogs,
    text: "Audit logs",
    id: "audit-logs",
    icon: "history",
  },
  {
    href: () => Routes.team.settings(),
    text: "Permissions",
    id: "settings",
    icon: "users",
  },
  {
    href: () => Routes.team.settings("sso"),
    text: "Single Sign-On",
    id: "sso",
    icon: "scanFace",
  },
];
